import { Component, ElementRef, OnInit, QueryList, ViewChildren } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

import { ProjectService } from "../shared/project/project.service";
import { BenchmarkDiagramPoint, BenchmarkDiagramSeries, BenchmarksModel, DriverTypeForecastAdjustmentViewItem, FcstAdjStrategy } from "../shared/benchmarks/benchmarks-model";
import { DataService } from "../shared/network/data.service";
import { AggregationMethod, AggregationType } from "../shared/preaggregation/aggregation-method";
import { ColumnName } from "../shared/import/column-name";
import { RoundingHelper } from "../shared/rounding/rounding-helper";
import { FancyNumberPipe } from "../shared/directives/fancy-number.pipe";
import { ForecastAdjustmentViewItem } from "../shared/benchmarks/forecast-adjustment-view-item";
import { FormControl, FormGroup } from "@angular/forms";

@Component({
    templateUrl: './benchmarks.component.html',
    styleUrls: ['./benchmarks.component.scss', '../cards/cards.component.scss']
})
export class BenchmarksComponent implements OnInit {
    AggregationType: typeof AggregationType = AggregationType;   //hack for enum to be available in the template
    FcstAdjStrategy: typeof FcstAdjStrategy = FcstAdjStrategy;
    @ViewChildren("pointValInput") pointValInputs: QueryList<ElementRef>;
    public loading = true;
    public seqMode = true;
    private tabId: string;
    private tabIx: number | null;
    public mainfcstwalk: boolean;
    private cardIndex: number;
    public projectId: string;
    public viewId: string;
    private formCompIx: number;

    public showSidebar = false;
    public isForecastAdjustmentDirty = false;
    public isWeightsDirty = false;
    public showWeightings = false;
    public showWeightAllShortcut = false;
    public lastSelectedWeightColumn = 0;

    public pointWidth = 70;
    public viewBoxWidth = 1000;
    public viewBoxHeight = 400;
    public showLegendForArrows = false;
    public adjustedSeries: BenchmarkDiagramSeries = undefined;
    public showAdjustedForecast = false;

    public returnTabId: string;
    public returnViewId: string;
    public returnTabIx: number | undefined;

    public diagramMaxVal = 100;
    public diagramMinVal = 0;
    public xStep: number = 100;
    public yStep: number = 20;
    public verticalGridLines = [];  //fake array to loop through in the template
    public horizontalGridLines = [];
    public linearLeftX: number = undefined;
    public linearLeftY: number = undefined;
    public linearRightX: number = undefined;
    public linearRightY: number = undefined;
    public bandTetragon: string = undefined;
    public diagramMinMaxCtx = FancyNumberPipe.createDefaultContext();
    public fcstAdjustmentCtx = {
        scale: 'auto',
        roundTo: 'auto',
        showPlusForPositive: true,
        use2stepRounding: true,
    };
    public formCompAdjCtx = {
        roundTo: 'auto',
        scale: 'auto',
        showPlusForPositive: true,
        use2stepRounding: true,
    };
    public curDriverformCompAdjCtx = FancyNumberPipe.createDefaultContext();
    public outcomeImpactCtx = FancyNumberPipe.createDefaultContext();
    public diagramPointCtx = FancyNumberPipe.createDefaultContext();

    public model: BenchmarksModel;
    public columnNames: string[] = [];
    public haveAdjustments = false;
    public currentForecastAdjustment: ForecastAdjustmentViewItem;

    form = new FormGroup({
        strategyFrmCtrl: new FormControl(),
    });

    public xlsxDownloadLink: string = '';

    constructor(private activatedRoute: ActivatedRoute, private projectService: ProjectService, private dataService: DataService,
        private router: Router) {}

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((queryParams: any) => {
            //console.log('magic data 2:');
            //console.log(history.state);
            this.projectId = queryParams.projectId;
            let cardIndex = queryParams.cardIndex;
            this.showWeightings = !!queryParams.weights;
            let missingParams = false;
            if (this.showWeightings) {
                this.showSidebar = true;
            }
            this.mainfcstwalk = 'true' == queryParams.mainfcstwalk;
            if (this.mainfcstwalk) {
                let tabIx = this.parseStringInt(queryParams.tabIx);
                if (tabIx === null && cardIndex === undefined) {
                    missingParams = true;
                } else {
                    this.tabIx = tabIx;
                    this.cardIndex = cardIndex;
                    this.formCompIx = queryParams.formCompIx;
                }
                this.returnTabIx = history.state.returnTabIx;
            } else {
                let viewId = queryParams.viewId;
                let tabId = queryParams.tabId;
                let formCompIx = queryParams.formCompIx;
                if (viewId && cardIndex != undefined) {
                    this.viewId = viewId;
                    this.tabId = tabId;
                    this.cardIndex = cardIndex;
                    this.formCompIx = formCompIx;
                    if (history.state.initSeqMode !== undefined) {
                        this.seqMode = history.state.initSeqMode;
                    }
                    this.returnViewId = history.state.returnViewId;
                    this.returnTabId = history.state.returnTabId;
    
                } else {
                    missingParams = true;
                }
            }
            if (missingParams) {
                this.router.navigateByUrl(`cards?projectId=${this.projectId}`);
            } else {
                this.loadData();
            }
            
        });
    }

    private parseStringInt(str): number | null {
        let num = parseInt(str);
    
        if (isNaN(num)) {
            return null;
        } else {
            return num;
        }
    }

    private findOriginalSeries(series: BenchmarkDiagramSeries, model: BenchmarksModel = undefined): BenchmarkDiagramSeries {
        return this.findRelatedSeries(series, false, model);
    }

    private findAdjustedSeries(series: BenchmarkDiagramSeries, model: BenchmarksModel = undefined): BenchmarkDiagramSeries {
        return this.findRelatedSeries(series, true, model);
    }

    private findRelatedSeries(series: BenchmarkDiagramSeries, adjusted: boolean, model: BenchmarksModel = undefined): BenchmarkDiagramSeries {
        let mdl = model ? model : this.model;
        if (!mdl || !mdl.diagramSeriesSeq) {
            return undefined;
        }
        return mdl.diagramSeriesSeq.find(s => s.name == series.name && s.isAdjustedForecast === adjusted);
    }

    private initModel(model: BenchmarksModel): void {
        let self = this;
        function initSeries(series: BenchmarkDiagramSeries[]) {
            for (let i = 0; i < series.length; ++i) {
                let originalSeries = self.findOriginalSeries(series[i], model);
                for (let j = 0; j < series[i].points.length; ++j) {
                    series[i].points[j].originalValue = series[i].points[j].value;
                    if (series[i].isAdjustedForecast) {
                        self.haveAdjustments = true;
                        if (originalSeries && originalSeries.points[j].value != series[i].points[j].value) {
                            series[i].points[j].canBeDeleted = true;
                        }
                    }
                }
            }
        }
        initSeries(model.diagramSeriesTot);
        initSeries(model.diagramSeriesSeq);
        
        model.numericalColumns.splice(0, 0, { index: null, name: 'None' });
        model.diagramSeriesWithoutAdjusted = model.diagramSeriesSeq.filter(s => !s.isAdjustedForecast);
    }

    private loadModel(model: BenchmarksModel): void {
        let series = this.seqMode ? model.diagramSeriesSeq : model.diagramSeriesTot;

        let allNums: number[] = [];
        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                if (series[i].points[j]) {
                    allNums.push(series[i].points[j].value);
                }
            }
        }
        let diagramRoundingSettings = RoundingHelper.CalcRoundingSettingsForDiagram(allNums);

        let minVal = diagramRoundingSettings.minAxis;
        let maxVal = diagramRoundingSettings.maxAxis;
        this.diagramMinMaxCtx = {
            roundTo: diagramRoundingSettings.roundTo,
            showPlusForPositive: false,
            cap: undefined,
            scale: diagramRoundingSettings.scale,
            use2stepRounding: true,
        };

        const minControlValsCountForWidth = 1;
        let width = 1;
        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                if (!series[i].points[j]) {
                    continue;
                }
                if (width < series[i].points[j].xPos + 1) {
                    width = series[i].points[j].xPos + 1;
                }
            }
        }
        let xStep = this.viewBoxWidth / (Math.max(width, minControlValsCountForWidth) + 0);
        let verticalGap = 0;
        let yStep = (this.viewBoxHeight - 2 * verticalGap) / (maxVal - minVal);
        
        let self = this;
        function convertY(y : number) {
            return self.viewBoxHeight - verticalGap - yStep * (y - minVal);
        }
        function convertX(x: number) {
            return xStep * (x + .5)
        }

        for (let i = 0; i < series.length; ++i) {
            for (let j = 0; j < series[i].points.length; ++j) {
                let curPoint = series[i].points[j];
                if (!curPoint) {
                    continue;
                }
                curPoint.x = convertX(curPoint.xPos);
                curPoint.y = convertY(curPoint.value);
            }
        }
        for (let i = 0; i < model.forecastAdjustments.length; ++i) {
            if (model.forecastAdjustments[i].driverIdStr == model.driverId) {
                this.currentForecastAdjustment = model.forecastAdjustments[i];
            }
        }

        this.diagramMinVal = diagramRoundingSettings.roundTo * Math.floor((minVal - verticalGap / yStep) / diagramRoundingSettings.roundTo);
        this.diagramMaxVal = diagramRoundingSettings.roundTo * Math.ceil((maxVal + verticalGap / yStep) / diagramRoundingSettings.roundTo);
        this.xStep = xStep;
        this.yStep = yStep;
        this.model = model;
        this.createGrid();
        this.setShowArrowsForPoints();
        this.allowAdjustingTooClosePoints();
        if (series && series[0].bandTetragon) {
            let tetragon = series[0].bandTetragon;
            this.bandTetragon = `${convertX(tetragon.leftX)},${convertY(tetragon.topLeftY)} ${convertX(tetragon.rightX)},${convertY(tetragon.topRightY)} ${convertX(tetragon.rightX)},${convertY(tetragon.bottomRightY)} ${convertX(tetragon.leftX)},${convertY(tetragon.bottomLeftY)}`;
            let band = (tetragon.topLeftY - tetragon.bottomLeftY) / 2;
            this.linearLeftX = convertX(tetragon.leftX);
            //this.linearLeftY = convertY(tetragon.topLeftY - band);
            this.linearLeftY = convertY(tetragon.linearLeftY);
            this.linearRightX = convertX(tetragon.rightX);
            //this.linearRightY = convertY(tetragon.topRightY - band);
            this.linearRightY = convertY(tetragon.linearRightY);
        }
        this.loadColumnNames(model.numericalColumns);
        this.createXlsxDownLoadLink();
        this.initFancyNumCtxs();
        this.loading = false;
    }

    private initFancyNumCtxs(): void {
        let curDriverformCompAdjNums = [];
        let curDriverFormCompAdj = this.model.sideView.formulaComponents.find(fc => fc.formCompAdjFromPreviousTabs || fc.formCompAdjFromThisTab);
        if (curDriverFormCompAdj) {
            if (curDriverFormCompAdj.formCompAdjFromPreviousTabs) {
                curDriverformCompAdjNums.push(curDriverFormCompAdj.formCompAdjFromPreviousTabs);
            }
            if (curDriverFormCompAdj.formCompAdjFromThisTab) {
                curDriverformCompAdjNums.push(curDriverFormCompAdj.formCompAdjFromThisTab);
            }
        }
        let curDriverformCompAdjRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(curDriverformCompAdjNums);
        this.curDriverformCompAdjCtx = {
            roundTo: curDriverformCompAdjRoundingSettings.roundTo,
            scale: curDriverformCompAdjRoundingSettings.scale,
            showPlusForPositive: true,
            use2stepRounding: true,
        };

        let outcomeImpacts = [];
        if (this.model.sideView.formulaComponents) {
            outcomeImpacts = outcomeImpacts.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpact).map(fc => fc.outcomeImpact));
            outcomeImpacts = outcomeImpacts.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpactFromThisTab).map(fc => fc.outcomeImpactFromThisTab));
            outcomeImpacts = outcomeImpacts.concat(this.model.sideView.formulaComponents.filter(fc => fc.outcomeImpactFromPreviousTabs).map(fc => fc.outcomeImpactFromPreviousTabs));
        }
        let outcomeImpactsRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(outcomeImpacts);
        this.outcomeImpactCtx = {
            roundTo: outcomeImpactsRoundingSettings.roundTo,
            scale: outcomeImpactsRoundingSettings.scale,
            showPlusForPositive: true,
            use2stepRounding: true,
        };

        let diagramPointsNums = [ this.diagramMaxVal ];
        for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
            for (let j = 0; j < this.model.diagramSeriesSeq[i].points.length; ++j) {
                diagramPointsNums.push(this.model.diagramSeriesSeq[i].points[j].value);
            }
        }
        let diagramPointsRoundingSettings = RoundingHelper.CalcRoundingSettingsForNums(diagramPointsNums);
        this.diagramPointCtx = {
            roundTo: diagramPointsRoundingSettings.roundTo,
            scale: diagramPointsRoundingSettings.scale,
            showPlusForPositive: false,
            use2stepRounding: true,};
    }

    private createGrid(): void {
        this.verticalGridLines = new Array(Math.floor(this.viewBoxWidth / this.xStep) + 1);
        this.horizontalGridLines = new Array(8);
    }

    private pointsAreEqual(val1: number, val2: number): boolean {
        return Math.abs(val1-val2) < 0.000000000001;
    }
    
    private setShowArrowsForPoints() {
        this.showLegendForArrows = false;
        for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
            let ser = this.model.diagramSeriesSeq[i];
            if (ser.isAdjustedForecast) {
                continue;
            }
            let adjSeries = this.findAdjustedSeries(ser, this.model);
            for (let j = 0; j < ser.points.length; ++j) {
                ser.points[j].showArrows = ser.points[j].isSelectedAsForecast && (!adjSeries || this.pointsAreEqual(ser.points[j].value,adjSeries.points[j].value));
                if (ser.points[j].showArrows) {
                    this.showLegendForArrows = true;
                }
            }
        }
        this.adjustedSeries = this.model.diagramSeriesSeq.find(s => s.isAdjustedForecast);
        if (!this.adjustedSeries) {
            return;
        }
        let originalSeries = this.findOriginalSeries(this.adjustedSeries);
        this.showAdjustedForecast = false;
        for (let i = 0; i < originalSeries.points.length; ++i) {
            if (originalSeries.points[i].value != this.adjustedSeries.points[i].value) {
                this.showAdjustedForecast = true;
                break;
            }
        }
    }

    private allowAdjustingTooClosePoints(): void {
        //for precision loss cases
        for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
            let ser = this.model.diagramSeriesSeq[i];
            if (ser.isAdjustedForecast) {
                continue;
            }
            let adjSeries = this.findAdjustedSeries(ser, this.model);
            if (adjSeries) {
                for (let j = 0; j < ser.points.length; ++j) {
                    ser.points[j].isAdjustable = this.pointAreTooClose(ser.points[j].value, adjSeries.points[j].value);
                }
            }
        }
    }

    pointClick(point: BenchmarkDiagramPoint) {
        if (!point.isAdjustable || !this.seqMode) {
            return;
        }
        point.editing = true;
        setTimeout(() => {
            let firstInput = this.pointValInputs.toArray()[0];
            firstInput.nativeElement.focus();
        }, 1);
    }

    applyAdjustment(seriesIx: number, point: BenchmarkDiagramPoint) {
        this.isForecastAdjustmentDirty = true;
        point.value = +point.value;
        let series = this.model.diagramSeriesSeq[seriesIx];
        let adjustedSeries = this.findAdjustedSeries(series);
        if (!series.isAdjustedForecast && !adjustedSeries) {
            if (point.value != point.originalValue) {
                let adjSeries: BenchmarkDiagramSeries = {
                    name: series.name,
                    forecastNameSuffix: ' (adj. forecast)',
                    color: series.adjColor,
                    adjColor: series.adjColor,
                    isAdjustedForecast: true,
                    points: [],
                    minVal: -1,
                    maxVal: -1,
                    oldControlValX: -1,
                    oldControlValY: -1,
                    newControlValX: -1,
                    newControlValY: -1,
                    bandTetragon: undefined,
                    bandTetragonStr: undefined,
                };
                for (let i = 0; i < series.points.length; ++i) {
                    let curPoint = series.points[i];
                    let newVal = curPoint.controlValue === point.controlValue ? point.value : curPoint.value;
                    let newPoint: BenchmarkDiagramPoint = {
                        canBeDeleted: newVal != curPoint.originalValue,
                        controlValue: curPoint.controlValue,
                        value: newVal,
                        originalValue: curPoint.originalValue,
                        editing: false,
                        isAdjustable: curPoint.isAdjustable,
                        xPos: curPoint.xPos,
                        x: -1,
                        y: -1,
                        isSelectedAsForecast: true,
                        showArrows: false,
                    };
                    curPoint.isAdjustable = curPoint.isSelectedAsForecast;
                    adjSeries.points.push(newPoint);
                }
                point.isAdjustable = this.pointAreTooClose(point.value, point.originalValue);
                point.value = point.originalValue;
                this.model.diagramSeriesSeq.splice(seriesIx, 0, adjSeries);
            }
        } else if (!series.isAdjustedForecast && adjustedSeries) {
            var pt = adjustedSeries.points.find(p => p.controlValue == point.controlValue);
            pt.value = point.value;
            pt.canBeDeleted = true;
            point.isAdjustable = this.pointAreTooClose(point.value, point.originalValue);
            point.value = point.originalValue;
        }
        else {
            let originalSeries = this.findOriginalSeries(series);
            let originalPoint = originalSeries.points.find(p => p.controlValue == point.controlValue);
            point.canBeDeleted = point.value != originalPoint.value;
            originalPoint.isAdjustable = this.pointAreTooClose(point.value, originalPoint.value);
        }
        point.editing = false;
        this.loadModel(this.model);
    }

    private pointAreTooClose(val1: number, val2: number): boolean {
        return Math.abs(val1 - val2) < (this.diagramMaxVal - this.diagramMinVal) * 0.04;
    }

    removeForecastAdjust(seriesIx: number, pointIx: number) {
        if (!this.seqMode) {
            return;
        }

        let point = this.model.diagramSeriesSeq[seriesIx].points[pointIx];
        if (point.canBeDeleted) {
            let originalSeries = this.findOriginalSeries(this.model.diagramSeriesSeq[seriesIx]);
            let originalPoint = originalSeries.points.find(p => p.controlValue == point.controlValue);
            point.value = originalPoint.value;
            point.canBeDeleted = false;
            originalPoint.isAdjustable = true;
            this.applyAdjustment(seriesIx, point);
        }
    }

    async save() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            let adjustments = [];
            for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
                let series = this.model.diagramSeriesSeq[i];
                if (!series.isAdjustedForecast) {
                    continue;
                }
                for (let j = 0; j < series.points.length; ++ j) {
                    let point = series.points[j];
                    adjustments.push({
                        controlValue: point.controlValue,
                        adjustedValue: point.value,
                    });
                }
            }
            if (this.mainfcstwalk) {
                let res = await this.dataService.post<any>('benchmarks/saveForecastAdjustmentsForMainFcstWalk', {
                    projectId: this.projectId,
                    formCompIx: this.formCompIx,
                    tabIx: this.tabIx,
                    cardIndex: this.cardIndex,
                    adjustments: adjustments,
                    strategy: this.form.controls.strategyFrmCtrl.value,
                });
                if (this.cardIndex != res.cardIndex || this.tabIx != res.tabIx) {
                    this.router.navigate(
                        ['/benchmarks'],
                        { queryParams: {
                            projectId: this.projectId,
                            mainfcstwalk: true,
                            tabIx: res.tabIx,
                            cardIndex: res.cardIndex,
                        }});
                } else {
                    this.loadData();
                }
            } else {
                let curProjectId = this.projectId;
                let curViewId = this.viewId;
                let curCardIx = this.cardIndex;
                let curTabId = this.tabId;
                let res = await this.dataService.post<any>('benchmarks/saveForecastAdjustments', {
                    viewId: this.viewId,
                    driverId: this.model.driverId,
                    adjustments: adjustments,
                    formCompIx: this.formCompIx,
                    tabId: this.tabId,
                    cardIndex: this.cardIndex,
                    strategy: this.form.controls.strategyFrmCtrl.value,
                });
                if (curCardIx != res.cardIndex || curTabId != res.tabId) {
                    this.router.navigate(
                        ['/benchmarks'],
                        { queryParams: {
                            projectId: curProjectId,
                            viewId: curViewId,
                            cardIndex: res.cardIndex,
                            tabId: res.tabId,
                        }});
                } else {
                    this.loadData();
                }
            }
        } catch {
            this.loading = false;
        }
    }

    loadData() {
        this.loading = true;
        this.form.controls.strategyFrmCtrl.setValue(FcstAdjStrategy.Set);
        this.projectService.setProjectId(this.projectId);
        let url = this.mainfcstwalk
                  ? `benchmarksForMainFcstWalk?projectId=${this.projectId}&tabIx=${this.tabIx}&cardIndex=${this.cardIndex}&formCompIx=${this.formCompIx}`
                  : `benchmarks?viewId=${this.viewId}&tabId=${this.tabId}&cardIndex=${this.cardIndex}&formCompIx=${this.formCompIx}`;
        this.dataService.get<BenchmarksModel>(url).then(res => {
            this.initModel(res);
            this.loadModel(res);
            this.isForecastAdjustmentDirty = false;
            this.isWeightsDirty = false;
        }).catch(err =>  {
            console.error(err);
            this.router.navigateByUrl('/cards?projectId=' + this.projectId);
        });

    }

    private loadColumnNames(columnNames: ColumnName[]) {
        let length = columnNames.reduce((m, c) => Math.max(m, c.index), 0) + 1;
        let arr = new Array<string>(length);
        for (let i = 0; i < columnNames.length; ++i) {
            arr[columnNames[i].index] = columnNames[i].name;
        }
        this.columnNames = arr;
    }

    public toggleAggregationType(formComp: AggregationMethod): void {
        switch(formComp.type) {
            case AggregationType.sum:
                formComp.type = AggregationType.average;
                break;
            case AggregationType.average:
            case AggregationType.averageWeighted:
                formComp.type = AggregationType.sum;
                break;
        }
        formComp.columnIndex = null;
        this.isWeightsDirty = true;
    }

    public onNumericColumnChange($event: any, formComp: AggregationMethod) {
        this.isWeightsDirty = true;
        if (formComp.columnIndex || formComp.columnIndex === 0) {
            formComp.type = AggregationType.averageWeighted;
        } else {
            formComp.type = AggregationType.average;
        }
        let uniqueWightColumns = new Set(this.model.aggregationMethods.filter(am => am.type != AggregationType.sum)
                                     .map(am => am.columnIndex));
        this.showWeightAllShortcut = uniqueWightColumns.size > 1;
        this.lastSelectedWeightColumn = formComp.columnIndex;
    }

    public weightAllFormCompsByTheSameCol() {
        for (let i = 0; i < this.model.aggregationMethods.length; ++i) {
            if (this.model.aggregationMethods[i].type != AggregationType.sum) {
                this.model.aggregationMethods[i].columnIndex = this.lastSelectedWeightColumn;
            }
        }
    }

    public async saveWeights() {
        if (this.loading) {
            return;
        }
        try {
            this.loading = true;
            await this.dataService.post('benchmarks/saveWeights', {
                projectId: this.projectId,
                formulaComponentAggregationMethods: this.model.aggregationMethods,
            });
            this.isWeightsDirty = false;
            this.showWeightAllShortcut = false;
            this.loadData();
        } finally {
            this.loading = false;
        }
    }
    
    public getSummedFormComps() {
        if (!this.model || !this.model.aggregationMethods) {
            return '';
        }
        let result = '';
        for (let i = 0; i < this.model.aggregationMethods.length; ++i) {
            if (this.model.aggregationMethods[i].type === AggregationType.sum) {
                if (result == '') {
                    result += `[${this.model.formulaComponentNames[i]}]`;
                } else {
                    result += `, [${this.model.formulaComponentNames[i]}]`;
                }
            }
        }
        return result;
    }

    public getAveragedUp() {
        if (!this.model || !this.model.aggregationMethods || !this.columnNames) {
            return '';
        }
        let notWeighted = [];
        let weighted = {};
        function addWeightedBy(weightByCol: string, whatIsWeighted: string) {
            if (!weighted[weightByCol]) {
                weighted[weightByCol] = [];
            }
            weighted[weightByCol].push(whatIsWeighted);
        }
        for (let i = 0; i < this.model.aggregationMethods.length; ++i) {
            let curFormComp = this.model.aggregationMethods[i];
            if (curFormComp.type === AggregationType.average) {
                notWeighted.push(this.model.formulaComponentNames[i]);
            } else if (curFormComp.type === AggregationType.averageWeighted) {
                addWeightedBy(this.columnNames[curFormComp.columnIndex], this.model.formulaComponentNames[i]);
            }
        }
        let result = '';
        let weightCols = Object.keys(weighted);
        for (let i = 0; i < weightCols.length; ++i) {
            if (i > 0) {
                if (i < weightCols.length - 1) {
                    result += ', ';
                } else {
                    result += ' and ';
                }
            }
            result += ' average up ';
            for (let j = 0; j < weighted[weightCols[i]].length; ++j) {
                if (j > 0) {
                    if (j < weighted[weightCols[i]] - 1) {
                        result += ', ';
                    } else {
                        result += ' and ';
                    }
                }
                result += `[${weighted[weightCols[i]][j]}]`;
            }
            result += ` weighted by [${weightCols[i]}]`;
        }
        if (notWeighted.length) {
            if (result != '') {
                result += ', ';
            }
            result += 'average up ';
        }
        for (let i = 0; i < notWeighted.length; ++i) {
            if (i > 0) {
                if (i < notWeighted.length - 1) {
                    result += ', ';
                } else {
                    result += ' and ';
                }
            }
            result += `[${notWeighted[i]}]`;
            if (i == notWeighted.length - 1) {
                result += ' not weighted'
            }
        }
        return result;
    }
    
    async undo() {
        if (this.loading || !this.model.undoAvailable) {
            return;
        }
        try {
            this.loading = true;
            await this.dataService.post('processing/UndoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: false,
            });
            this.loadData();
        } catch {
            this.loading = false;
        }
    }

    async redo() {
        if (this.loading || !this.model.redoAvailable) {
            return;
        }
        try {
            this.loading = true;
            await this.dataService.post('processing/RedoMainFcstWalk', {
                projectId: this.projectId,
                loadMainFcstWalkModel: false,
            });
            this.loadData();
        } catch {
            this.loading = false;
        }
    }

    public driverTypeForecastAdjustmentOutcomeClick() {
        if (this.model.outcomeName == this.model.driverTypeName ) {
            return;
        }
        this.loading = true;
        let url: string;
        if (this.mainfcstwalk) {
            url = `benchmarks?projectId=${this.projectId}&mainfcstwalk=true&tabIx=${this.tabIx}&cardIndex=${this.cardIndex}&formCompIx=-1`;
        } else {
            url = `/benchmarks?projectId=${this.projectId}&viewId=${this.viewId}&cardIndex=${this.cardIndex}&formCompIx=${-1}`;
            if (this.tabId) {
                url += '&tabId=' + this.tabId;
            }
        }
        this.router.navigateByUrl(url);
    }

    public driverTypeForecastAdjustmentViewItemClick(item: DriverTypeForecastAdjustmentViewItem) {
        if (item.driverTypeName == this.model.driverTypeName ) {
            return;
        }
        this.loading = true;
        let url: string;
        if (this.mainfcstwalk) {
            url = `benchmarks?projectId=${this.projectId}&mainfcstwalk=true&tabIx=${this.tabIx}&cardIndex=${this.cardIndex}&formCompIx=${item.formulaComponentIndex}`;
        } else {
            url = `/benchmarks?projectId=${this.projectId}&viewId=${this.viewId}&cardIndex=${this.cardIndex}&formCompIx=${item.formulaComponentIndex}`;
            if (this.tabId != undefined && this.tabId != null) {
                url += '&tabId=' + this.tabId;
            }
        }
        this.router.navigateByUrl(url);
    }

    public haveForecast() {
        if (!this.model || !this.model.diagramSeriesSeq || !this.model.diagramSeriesSeq.length) {
            return false;
        }
        for (let i = 0; i < this.model.diagramSeriesSeq.length; ++i) {
            for (let j = 0; j < this.model.diagramSeriesSeq[i].points.length; ++j) {
                if (this.model.diagramSeriesSeq[i].points[j].isAdjustable) {
                    return true;
                }
            }
        }
        return false;
    }
    
    private createXlsxDownLoadLink() {
        this.xlsxDownloadLink = `${this.dataService.getBaseUrl()}/Processing/DownloadXlsx?viewId=${this.viewId}&allDrivers=true`;
    }

    public setSeqMode(val: boolean): void {
        this.seqMode = val;
        this.loadModel(this.model);
    }
}
